/**
 * Fullscreen Suggestion Overlay for Poker Reborn
 *
 * Shown once per session on small screens (< 400px) when player
 * enters Lobby or Room. Offers to enable fullscreen mode for
 * a better mobile experience.
 *
 * Lives in ScreenManager's overlayContainer (above all screens).
 *
 * @see docs/common/FULLSCREEN_SUGGESTION.md
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '../Button';
import { enterFullscreen } from '@/utils/fullscreen';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  panel: {
    width: 280,
    height: 200,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
    padding: 20,
  },
  title: {
    fill: 0xffffff,
    fontSize: 20,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  subtitle: {
    fill: 0x999999,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  enableButton: {
    width: 200,
    height: 40,
  },
  dismissButton: {
    width: 200,
    height: 36,
  },
};

// =============================================================================
// FULLSCREEN SUGGESTION OVERLAY
// =============================================================================

export class FullscreenSuggestionOverlay extends Container {
  private overlayBackground: Graphics;
  private panel: Container;

  private screenWidth: number;
  private screenHeight: number;

  constructor() {
    super();

    this.screenWidth = 0;
    this.screenHeight = 0;
    this.visible = false;

    // Full-screen overlay background (tap to dismiss)
    this.overlayBackground = new Graphics();
    this.overlayBackground.eventMode = 'static';
    this.overlayBackground.cursor = 'pointer';
    this.overlayBackground.on('pointertap', () => { this.hide(); });
    this.addChild(this.overlayBackground);

    // Centered panel
    this.panel = new Container();
    this.addChild(this.panel);

    // Panel background
    const panelBackground = new Graphics();
    panelBackground.roundRect(0, 0, STYLES.panel.width, STYLES.panel.height, STYLES.panel.cornerRadius);
    panelBackground.fill(STYLES.panel.fill);
    panelBackground.setStrokeStyle({ width: 2, color: STYLES.panel.stroke });
    panelBackground.stroke();
    panelBackground.eventMode = 'static';
    panelBackground.on('pointerdown', (e) => { e.stopPropagation(); });
    this.panel.addChild(panelBackground);

    // Title
    const titleText = new Text({
      text: 'Go Fullscreen?',
      style: STYLES.title,
    });
    titleText.anchor.set(0.5, 0);
    titleText.x = STYLES.panel.width / 2;
    titleText.y = STYLES.panel.padding;
    this.panel.addChild(titleText);

    // Subtitle
    const subtitleText = new Text({
      text: 'For the best experience on mobile',
      style: STYLES.subtitle,
    });
    subtitleText.anchor.set(0.5, 0);
    subtitleText.x = STYLES.panel.width / 2;
    subtitleText.y = STYLES.panel.padding + 30;
    this.panel.addChild(subtitleText);

    // Enable Fullscreen button (gold)
    const enableButton = new Button({
      label: 'Enable Fullscreen',
      width: STYLES.enableButton.width,
      height: STYLES.enableButton.height,
      style: GOLD_BUTTON_STYLE,
    });
    enableButton.x = (STYLES.panel.width - STYLES.enableButton.width) / 2;
    enableButton.y = STYLES.panel.padding + 70;
    enableButton.onClick = () => {
      enterFullscreen();
      this.hide();
    };
    this.panel.addChild(enableButton);

    // Not Now button (secondary)
    const dismissButton = new Button({
      label: 'Not Now',
      width: STYLES.dismissButton.width,
      height: STYLES.dismissButton.height,
      style: SECONDARY_BUTTON_STYLE,
    });
    dismissButton.x = (STYLES.panel.width - STYLES.dismissButton.width) / 2;
    dismissButton.y = STYLES.panel.padding + 120;
    dismissButton.onClick = () => {
      this.hide();
    };
    this.panel.addChild(dismissButton);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Show the overlay */
  show(): void {
    this.visible = true;
  }

  /** Hide the overlay */
  hide(): void {
    this.visible = false;
  }

  /** Resize overlay and re-center panel */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Redraw overlay background
    this.overlayBackground.clear();
    this.overlayBackground.rect(0, 0, width, height);
    this.overlayBackground.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Center panel
    this.panel.x = (width - STYLES.panel.width) / 2;
    this.panel.y = (height - STYLES.panel.height) / 2;
  }
}
